import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import { TReportUsersByUserGroupsData } from '@src/types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import {
  exportUsersByUserGroups,
  getUsersByUserGroups,
} from '@src/services/reports';
import { toast } from 'react-toastify';

const downloadCsvFile = (blobPart: BlobPart, fileName: string) => {
  const csvBlob = new Blob([blobPart], { type: 'text/csv;charset=utf-8;' });
  const csvUrl = URL.createObjectURL(csvBlob);
  const link = document.createElement('a');

  link.href = csvUrl;
  link.download = `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(csvUrl);
  document.body.removeChild(link);
};

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportUsersByUserGroupsData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] min-w-[150px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-white font-semibold mb-1">
          {payload[0]?.payload?.user_group}
        </p>
        <p className="text-[#B6D72C] text-lg font-bold">
          {payload[0]?.value?.toLocaleString()}
        </p>
        <p className="text-[#999999] text-[10px] mt-1">Users</p>
      </div>
    );
  }

  return null;
};

export const UserByUserGroups: FC = () => {
  const [data, setData] = useState<TReportUsersByUserGroupsData[]>([]);

  const handleRunReport = async () => {
    try {
      const response = await getUsersByUserGroups({});
      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error fetching report. Please try again.');
    }
  };

  const handleExportReport = async (format: 'csv' | 'xlsx') => {
    try {
      const response = await exportUsersByUserGroups(format);

      if (format === 'csv') {
        downloadCsvFile(response.data, 'users_by_user_groups');
      }

      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  // Use custom hook for table columns
  const { columns, tableData } =
    useReportTableColumns<TReportUsersByUserGroupsData>({
      data,
      getAccessField: (item: TReportUsersByUserGroupsData) => item.user_group,
      getMainDataField: (item: TReportUsersByUserGroupsData) =>
        item.user_count || 0,
      includeTotalsRow: true,
      totalsRowLabel: 'Total',
      customColumns: [
        {
          title: 'USER COUNT',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">
              {item?.user_count?.toLocaleString() ?? 0}
            </p>
          ),
        },
      ],
    });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Users by User Groups
      </h1>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="user_group"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />
              <Bar dataKey="user_count" fill="#B6D72C" name="User Count" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />

          <Divider />

          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => handleExportReport('csv')}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
